/**
 * GraphQL queries and hooks — businessEnvironmentFactor pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type {
  BusinessEnvironmentFactor,
  BusinessEnvironmentFactorConnection,
  BusinessEnvironmentFactorFilter,
} from '../types/businessEnvironmentFactor';

/**
 * Fragment for BusinessEnvironmentFactor fields
 */
export const BUSINESS_ENVIRONMENT_FACTOR_FIELDS = gql`
  fragment BusinessEnvironmentFactorFields on BusinessEnvironmentFactor {
    entityId
    entityType
    name
    description
    category
    label
    tags
    notes
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

/**
 * Query to get a single business environment factor by ID
 */
export const GET_BUSINESS_ENVIRONMENT_FACTOR = gql`
  ${BUSINESS_ENVIRONMENT_FACTOR_FIELDS}
  query GetBusinessEnvironmentFactor($id: ID!) {
    businessEnvironmentFactor(id: $id) {
      ...BusinessEnvironmentFactorFields
    }
  }
`;

/**
 * Query to list all business environment factors with filtering and pagination
 */
export const LIST_BUSINESS_ENVIRONMENT_FACTORS = gql`
  ${BUSINESS_ENVIRONMENT_FACTOR_FIELDS}
  query ListBusinessEnvironmentFactors(
    $filter: BusinessEnvironmentFactorFilter
    $first: Int
    $after: String
  ) {
    businessEnvironmentFactors(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...BusinessEnvironmentFactorFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Hook to get a single business environment factor
 */
export function useBusinessEnvironmentFactor(
  id: string
): QueryResult<{ businessEnvironmentFactor: BusinessEnvironmentFactor | null }> {
  return useQuery(GET_BUSINESS_ENVIRONMENT_FACTOR, {
    variables: { id },
    skip: !id,
  });
}

/**
 * Hook to list business environment factors with optional filtering.
 *
 * Fetches all pages (see {@link useAllPages}) so the list is not capped at one
 * server page.
 */
export function useBusinessEnvironmentFactors(
  filter?: BusinessEnvironmentFactorFilter,
  first: number = 250
): QueryResult<{ businessEnvironmentFactors: BusinessEnvironmentFactorConnection }> {
  return useAllPages<{ businessEnvironmentFactors: BusinessEnvironmentFactorConnection }>(
    LIST_BUSINESS_ENVIRONMENT_FACTORS,
    (data) => data?.businessEnvironmentFactors,
    { filter },
    first
  );
}
